import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import AuthModalHost from '@/features/auth/components/AuthModalHost';

export type AuthModalMode = 'signin' | 'register';

interface AuthModalContextValue {
  isOpen: boolean;
  mode: AuthModalMode;
  isAuthenticated: boolean;
  openAuthModal: (mode?: AuthModalMode, afterAuth?: () => void) => void;
  closeAuthModal: () => void;
  setAuthMode: (mode: AuthModalMode) => void;
  requireAuth: (action?: () => void) => void;
  notifyAuthenticated: () => void;
}

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AuthModalMode>('signin');
  const [isAuthenticated, setIsAuthenticated] = useState(() => apiService.isAuthenticated());
  const afterAuthRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const sync = () => setIsAuthenticated(apiService.isAuthenticated());
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, sync);
    return () => window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, sync);
  }, []);

  const openAuthModal = useCallback((nextMode: AuthModalMode = 'signin', afterAuth?: () => void) => {
    afterAuthRef.current = afterAuth ?? null;
    setMode(nextMode);
    setIsOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    afterAuthRef.current = null;
    setIsOpen(false);
  }, []);

  const setAuthMode = useCallback((nextMode: AuthModalMode) => {
    setMode(nextMode);
  }, []);

  const requireAuth = useCallback(
    (action?: () => void) => {
      if (apiService.isAuthenticated()) {
        action?.();
        return;
      }
      openAuthModal('signin', action);
    },
    [openAuthModal]
  );

  const notifyAuthenticated = useCallback(() => {
    setIsAuthenticated(true);
    setIsOpen(false);
    const pending = afterAuthRef.current;
    afterAuthRef.current = null;
    if (pending) setTimeout(pending, 0);
  }, []);

  return (
    <AuthModalContext.Provider
      value={{ isOpen, mode, isAuthenticated, openAuthModal, closeAuthModal, setAuthMode, requireAuth, notifyAuthenticated }}
    >
      {children}
      <AuthModalHost />
    </AuthModalContext.Provider>
  );
}

export function useAuthModal(): AuthModalContextValue {
  const ctx = useContext(AuthModalContext);
  if (!ctx) throw new Error('useAuthModal must be used within AuthModalProvider');
  return ctx;
}
